// @flow
import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { localize } from '@ta/common.i18n';
import { featureIsEnabled } from '@ta/common.features';
import { LinkPostUploader } from '@ta/social.linkpost-upload';
import { ugcActionMapper, SocialFab, LoginPID, type UgcAction } from '@ta/social.ugc-actions';
import { MediaUploaderModal } from '@ta/media.upload-exports';
import UgcActionSection from './UgcActionSection';

type ExternalProps = {|
  permissions: any,
  permissionsLoading: boolean,
  fullWidth: boolean,
  triggerFeedRefresh: () => void,
|};

type Props = {|
  ...ExternalProps,
  isLoggedIn?: boolean, // Provided by redux
|};

// Social Action list on Home
const SocialUgcActionSection = ({
  permissions,
  permissionsLoading,
  fullWidth,
  triggerFeedRefresh,
  isLoggedIn,
}: Props) => {
  const linkUploaderModalName = 'link-uploader';
  const linkPostingFeatureEnabled = featureIsEnabled('social_link_posting_enabled');
  const videoUploaderModalName = 'video-uploader';
  const videoFeatureEnabled = featureIsEnabled('media_video_upload_enabled');
  const photoUploaderModalName = 'photo-uploader';
  const photoFeatureEnabled = featureIsEnabled('media_photo_upload_enabled');
  const actions: UgcAction[] = [];

  if (featureIsEnabled('social_feedback_enabled')) {
    actions.push(ugcActionMapper('Feedback'));
  }

  const onSubmit = useCallback(() => {
    triggerFeedRefresh();
    // eslint-disable-next-line no-restricted-globals
    window.scrollTo(0, 0);
  }, [triggerFeedRefresh]);

  if (videoFeatureEnabled && permissions && permissions.canUploadVideo) {
    actions.push({
      label: localize('ugc_uploader_video_v3'),
      icon: 'video-fill',
      trackingName: 'POST_VIDEOS',
      name: videoUploaderModalName,
      // eslint-disable-next-line react/display-name
      overlay: () => (
        <MediaUploaderModal
          mediaType="video"
          modalName={videoUploaderModalName}
          linkUploaderModalName={linkUploaderModalName}
          onSubmit={onSubmit}
          pid={40488}
        />
      ),
    });
  }

  if (
    photoFeatureEnabled &&
    (featureIsEnabled('enable_post_photo_ugc_for_logout_user') || permissions?.canPostPhoto)
  ) {
    actions.push({
      label: localize('ugc_uploader_photo_v3'),
      icon: 'camera-fill',
      name: photoUploaderModalName,
      trackingName: 'POST_PHOTOS',
      pid: LoginPID.Photo,
      // eslint-disable-next-line react/display-name
      overlay: () => (
        <MediaUploaderModal
          mediaType="photo"
          modalName={photoUploaderModalName}
          linkUploaderModalName={linkUploaderModalName}
          onSubmit={triggerFeedRefresh}
          pid={40488}
        />
      ),
    });
  }

  // TRVX-9686: We need to make sure during the logged-out -> click WAR -> login flow, when permission are still loading, this action does not disappear
  if (
    featureIsEnabled('social_war_enabled') &&
    (!isLoggedIn || permissionsLoading || (permissions && permissions.canWriteReview))
  ) {
    actions.push({
      label: localize('ugc_uploader_review_v2'), // Write review
      icon: 'pencil-paper',
      trackingName: 'WRITE_REVIEW',
      url: '/UserReview',
      loginPid: 40468,
      pid: 40488,
    });
  }

  if (linkPostingFeatureEnabled && permissions && permissions.canPostLink) {
    actions.push({
      label: localize('ugc_uploader_linkPost_v3'), // Post links
      icon: 'link',
      name: linkUploaderModalName,
      trackingName: 'POST_LINKS',
      // eslint-disable-next-line react/display-name
      overlay: () => (
        <LinkPostUploader
          modalName={linkUploaderModalName}
          onSubmit={triggerFeedRefresh}
          displaySuccessModal={false}
        />
      ),
    });
  }

  if (fullWidth) {
    return <SocialFab actions={actions} />;
  }
  return <UgcActionSection actions={actions} />;
};

SocialUgcActionSection.defaultProps = {
  isLoggedIn: false,
};

export default connect<Props, ExternalProps, _, _, _, _>(redux => ({
  isLoggedIn: !!redux.auth.loggedInUserId,
}))(SocialUgcActionSection);
